/**
 * splitRecommender.js — Logique pure (aucun appel réseau) du créateur de programme débutant
 * "Choisir moi-même" : catalogue des types de split, génération des séances par split/fréquence,
 * et moteur de recommandation déterministe (fréquence + niveau + objectif déjà saisis dans le
 * questionnaire) qui calcule quel split mérite le badge "Recommandé".
 */

// Groupes musculaires de la grande bibliothèque (exercises_library_v2) — voir
// /app/backend/services/exercise_library_v2_data.py::MUSCLE_GROUPS.
export const CHEST = ['Pectoraux'];
export const BACK = ['Dos', 'Trapèzes'];
export const SHOULDERS = ['Épaules'];
export const ARMS = ['Biceps', 'Triceps', 'Avant-bras'];
export const LEGS = ['Quadriceps', 'Ischio-jambiers', 'Fessiers', 'Adducteurs / Abducteurs', 'Mollets'];
export const CORE_CARDIO = ['Abdominaux', 'Lombaires', 'Cardio'];

export const FULL_BODY_GROUPS = [...CHEST, ...BACK, ...SHOULDERS, ...LEGS.slice(0, 3), 'Abdominaux'];
export const UPPER_GROUPS = [...CHEST, ...BACK, ...SHOULDERS, ...ARMS];
export const LOWER_GROUPS = [...LEGS, 'Abdominaux'];
export const PUSH_GROUPS = [...CHEST, ...SHOULDERS, 'Triceps'];
export const PULL_GROUPS = [...BACK, 'Biceps', 'Avant-bras'];
export const LEGS_GROUPS = LEGS;

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

/** Répète un gabarit de base [{label, groups}] jusqu'à `days` séances, en ajoutant un suffixe
 * de lettre (A/B/C...) à chaque nouveau cycle complet — tronqué proprement si `days` ne tombe
 * pas juste (ex: Push/Pull/Legs sur 4 jours = Push, Pull, Legs, Push B). */
const cycleSessions = (base, days) => {
  const out = [];
  let cycle = 0;
  while (out.length < days) {
    base.forEach((b) => {
      if (out.length >= days) return;
      out.push({ label: cycle === 0 ? b.label : `${b.label} ${LETTERS[cycle]}`, groups: b.groups });
    });
    cycle += 1;
    if (cycle > 10) break; // garde-fou
  }
  return out;
};

export const SPLIT_TYPES = [
  {
    value: 'full_body', label: 'Full Body', shortDesc: 'Tout le corps à chaque séance',
    longDesc: 'Chaque séance travaille l’ensemble du corps. Idéal pour progresser vite en fréquence sans se disperser.',
    whyText: 'Pourquoi ce split ? Chaque groupe musculaire est stimulé 2 à 3 fois par semaine — parfait pour apprendre vite la bonne technique et progresser en force au début, même en prise de muscle.',
    idealDays: [1, 2, 3], bestLevels: ['Débutant'],
    bestGoals: ['Perte de poids', 'Remise en forme générale', 'Endurance musculaire', 'Préparation sportive / mobilité', 'Prise de muscle'],
    buildSessions: (days) => cycleSessions([{ label: 'Full Body', groups: FULL_BODY_GROUPS }], days),
  },
  {
    value: 'upper_lower', label: 'Half Body', shortDesc: 'Haut du corps / Bas du corps en alternance',
    longDesc: 'Une séance sur deux cible le haut du corps, l’autre le bas — bon équilibre volume/récupération.',
    whyText: 'Pourquoi ce split ? Chaque zone (haut/bas) récupère un jour complet entre 2 séances tout en étant travaillée souvent — un bon compromis volume/récupération dès 3-4 séances par semaine.',
    idealDays: [3, 4, 5], bestLevels: ['Débutant', 'Intermédiaire'],
    bestGoals: ['Prise de muscle', 'Remise en forme générale'],
    buildSessions: (days) => cycleSessions(
      [{ label: 'Haut du corps', groups: UPPER_GROUPS }, { label: 'Bas du corps', groups: LOWER_GROUPS }], days,
    ),
  },
  {
    value: 'push_pull_legs', label: 'Push Pull Legs', shortDesc: 'Pousser / Tirer / Jambes',
    longDesc: 'Le split le plus populaire en musculation : pousser (pecs/épaules/triceps), tirer (dos/biceps), jambes.',
    whyText: 'Pourquoi ce split ? Il sépare les mouvements par mécanique articulaire, ce qui permet plus de volume par groupe et une meilleure récupération — idéal quand on veut pousser l’intensité 3 à 6 fois/semaine.',
    idealDays: [3, 6], bestLevels: ['Intermédiaire', 'Avancé'],
    bestGoals: ['Prise de muscle', 'Augmentation de la force'],
    buildSessions: (days) => cycleSessions(
      [{ label: 'Push (Pecs/Épaules/Triceps)', groups: PUSH_GROUPS }, { label: 'Pull (Dos/Biceps)', groups: PULL_GROUPS }, { label: 'Jambes', groups: LEGS_GROUPS }], days,
    ),
  },
  {
    value: 'agonist_antagonist', label: 'Agoniste / Antagoniste', shortDesc: 'Paires de muscles opposés dans la même séance',
    longDesc: 'Chaque séance combine 2 groupes musculaires opposés (ex: pectoraux + dos) qui se reposent l’un pendant que l’autre travaille.',
    whyText: 'Pourquoi ce split ? Alterner un muscle et son opposé permet de garder un rythme soutenu (moins d’attente entre séries) tout en couvrant tout le corps sur la semaine — pratique à 4-5 séances.',
    idealDays: [4, 5], bestLevels: ['Intermédiaire', 'Avancé'],
    bestGoals: ['Prise de muscle'],
    buildSessions: (days) => cycleSessions([
      { label: 'Pectoraux & Dos', groups: ['Pectoraux', 'Dos', 'Trapèzes'] },
      { label: 'Quadriceps & Ischio-jambiers', groups: ['Quadriceps', 'Ischio-jambiers', 'Fessiers'] },
      { label: 'Épaules & Abdominaux', groups: ['Épaules', 'Abdominaux', 'Lombaires'] },
      { label: 'Biceps & Triceps', groups: ['Biceps', 'Triceps', 'Avant-bras'] },
      { label: 'Mollets & Cardio', groups: ['Mollets', 'Adducteurs / Abducteurs', 'Cardio'] },
    ], days),
  },
  {
    value: 'bro_split', label: 'Split (1 groupe/jour)', shortDesc: 'Un groupe musculaire majeur par séance',
    longDesc: 'Le split classique façon bodybuilding : un seul groupe musculaire par séance, poussé à fond avec beaucoup de volume.',
    whyText: 'Pourquoi ce split ? Concentrer tout le volume d’un groupe sur une seule séance permet une intensité et une fatigue localisée maximales — un format avancé qui demande déjà une bonne technique.',
    idealDays: [5, 6], bestLevels: ['Avancé'],
    bestGoals: ['Prise de muscle', 'Augmentation de la force'],
    buildSessions: (days) => cycleSessions([
      { label: 'Pectoraux', groups: CHEST },
      { label: 'Dos', groups: BACK },
      { label: 'Épaules', groups: SHOULDERS },
      { label: 'Jambes', groups: LEGS_GROUPS },
      { label: 'Bras', groups: ARMS },
      { label: 'Abdos & Cardio', groups: CORE_CARDIO },
    ], days),
  },
];

export const ALL_MUSCLE_GROUPS = [
  'Pectoraux', 'Dos', 'Épaules', 'Trapèzes', 'Biceps', 'Triceps', 'Avant-bras',
  'Abdominaux', 'Lombaires', 'Fessiers', 'Quadriceps', 'Ischio-jambiers', 'Mollets',
  'Adducteurs / Abducteurs', 'Cardio',
];

export const EQUIPMENT_TYPES = [
  { key: 'machine_guidee', label: 'Machine guidée' },
  { key: 'poulie', label: 'Poulie' },
  { key: 'barre_libre', label: 'Barre libre' },
  { key: 'halteres', label: 'Haltères' },
  { key: 'poids_du_corps', label: 'Poids du corps' },
  { key: 'petit_materiel', label: 'Petit matériel' },
];

/** Mappe le tag matériel "maison" (ancienne taxonomie de ProgramWizard) vers le slug matériel
 * de la grande bibliothèque (nouvelle taxonomie) — utilisé pour préremplir le filtre matériel
 * par défaut de chaque séance dans le parcours "Choisir moi-même". */
const HOME_TAG_TO_EQUIPMENT = {
  'Haltère': 'halteres', 'Kettlebell': 'petit_materiel', 'Accessoire': 'petit_materiel', 'Barre': 'barre_libre',
};

export const defaultEquipmentFor = (wizardData) => {
  if (!wizardData || wizardData.location === 'Salle de fitness' || wizardData.location === 'Hybride (salle + maison)') {
    return EQUIPMENT_TYPES.map((e) => e.key);
  }
  const tags = new Set(['poids_du_corps']);
  (wizardData.equipmentTags || []).forEach((t) => { if (HOME_TAG_TO_EQUIPMENT[t]) tags.add(HOME_TAG_TO_EQUIPMENT[t]); });
  return Array.from(tags);
};

export const SIMPLE_SET_TYPES = ['straight', 'superset', 'biset'];

export const getSplit = (value) => SPLIT_TYPES.find((s) => s.value === value) || SPLIT_TYPES[0];



/** Score déterministe (plus haut = plus adapté) — combine proximité de fréquence idéale,
 * correspondance de niveau et correspondance d'objectif. Le split au score max reçoit le badge
 * "Recommandé" ; l'utilisateur reste libre de choisir n'importe quel autre split manuellement. */
export const scoreSplit = (split, daysPerWeek, level, goal) => {
  const dist = Math.min(...split.idealDays.map((d) => Math.abs(d - daysPerWeek)));
  let score = Math.max(0, 10 - dist * 4);
  if (level && split.bestLevels.includes(level)) score += 6;
  if (goal && split.bestGoals.includes(goal)) score += 6;
  return score;
};

export const recommendSplitValue = (daysPerWeek, level, goal) => {
  let best = null;
  let bestScore = -Infinity;
  SPLIT_TYPES.forEach((s) => {
    const sc = scoreSplit(s, daysPerWeek, level, goal);
    if (sc > bestScore) { bestScore = sc; best = s.value; }
  });
  return best;
};

/** Suggestion du nombre d'exercices à viser pour un groupe musculaire donné (les gros groupes
 * "compound" méritent plus de volume que les petits groupes d'isolation). */
export const suggestedExerciseCount = (muscleGroup) => (
  ['Dos', 'Pectoraux', 'Quadriceps', 'Épaules'].includes(muscleGroup) ? 2 : 1
);
